import { persons as kycAcceptance } from '@repo/api-kit/kyc-acceptance';
import type { KycResponse } from '@repo/api-kit/queries/due-diligence/kyc';

const { ApiKycCurrentStatus, ApiProcessStatus, ApiProvider, ApiProcessType } =
  kycAcceptance.firstparty.v1;

export const MOCKED_FOURTHLINE_TOKEN = 'mock-fourthline-token-456';
export const MOCKED_FOURTHLINE_REDIRECT_URL = 'https://fourthline.example.com/verify';

interface CreateKycResponseWithFourthlineOptions {
  kycCurrentStatus?: KycResponse['kycCurrentStatus'];
  activeProcess?: null;
  processType?: (typeof ApiProcessType)[keyof typeof ApiProcessType];
  providerDetails?: null;
  processStatus?: (typeof ApiProcessStatus)[keyof typeof ApiProcessStatus];
  signatureRequired?: boolean;
  token?: string;
}

/**
 * Creates a mock KYC response with Fourthline provider details
 */
export function createKycResponseWithFourthline(
  options: Readonly<CreateKycResponseWithFourthlineOptions> = {}
): KycResponse {
  const {
    kycCurrentStatus = ApiKycCurrentStatus.pending,
    processType = ApiProcessType.initial,
    processStatus = ApiProcessStatus.pending_liveness,
    signatureRequired = false,
    token = MOCKED_FOURTHLINE_TOKEN,
  } = options;

  const providerDetails =
    options.providerDetails === null
      ? null
      : {
          $case: 'fourthlineProviderDetails' as const,
          value: {
            redirectUrl: MOCKED_FOURTHLINE_REDIRECT_URL,
            token,
          },
        };

  const activeProcess =
    options.activeProcess === null
      ? null
      : {
          type: processType,
          processStatus,
          provider: ApiProvider.fourthline,
          providerDetails,
          signatureRequired,
        };

  return {
    kycCurrentStatus,
    activeProcess,
  };
}

export function createPendingFourthlineKycResponse() {
  return createKycResponseWithFourthline();
}

export function createExpiredFourthlineKycResponse() {
  return createKycResponseWithFourthline({
    processType: ApiProcessType.re_kyc,
  });
}

export function createSubmittedFourthlineKycResponse() {
  return createKycResponseWithFourthline({
    processStatus: ApiProcessStatus.submitted,
    providerDetails: null,
  });
}

/**
 * Edge case: Token is empty string
 * This should never happen but tests error handling
 */
export function createEmptyTokenFourthlineResponse(): KycResponse {
  return {
    kycCurrentStatus: ApiKycCurrentStatus.pending,
    activeProcess: {
      type: ApiProcessType.initial,
      processStatus: ApiProcessStatus.pending_liveness,
      provider: ApiProvider.fourthline,
      providerDetails: {
        $case: 'fourthlineProviderDetails',
        value: {
          redirectUrl: MOCKED_FOURTHLINE_REDIRECT_URL,
          token: '',
        },
      },
      signatureRequired: false,
    },
  };
}

/**
 * Edge case: Redirect URL is empty string
 * This should never happen but tests error handling
 */
export function createEmptyRedirectUrlFourthlineResponse(): KycResponse {
  return {
    kycCurrentStatus: ApiKycCurrentStatus.pending,
    activeProcess: {
      type: ApiProcessType.initial,
      processStatus: ApiProcessStatus.pending_liveness,
      provider: ApiProvider.fourthline,
      providerDetails: {
        $case: 'fourthlineProviderDetails',
        value: {
          redirectUrl: '', // Empty redirect URL
          token: MOCKED_FOURTHLINE_TOKEN,
        },
      },
      signatureRequired: false,
    },
  };
}

export function createSignatureRequiredFourthlineResponse(): KycResponse {
  return createKycResponseWithFourthline({
    signatureRequired: true,
  });
}

export function createRefusedSubmittedFourthlineKycResponse() {
  return createKycResponseWithFourthline({
    kycCurrentStatus: ApiKycCurrentStatus.refused,
    processStatus: ApiProcessStatus.submitted,
    providerDetails: null,
  });
}
